import { z } from 'zod';
import { ROI_CALCULATOR_IDS, ROI_NUMBER_INPUTS, type RoiInputs } from './engine.js';

/**
 * An ROI project (brief sections 3 and 7.7): a calculator, a scenario name,
 * the inputs, the sections open on screen, and the record it is linked to.
 * The same rules on the client and the server.
 */

/** What a project may be linked to: a company, a contact or a deal, or a portal lead. */
export const ROI_LINK_KINDS = ['company', 'contact', 'deal', 'lead'] as const;
export type RoiLinkKind = (typeof ROI_LINK_KINDS)[number];

export const ROI_LINK_KIND_LABELS: Record<RoiLinkKind, string> = {
  company: 'Company',
  contact: 'Contact',
  deal: 'Deal',
  lead: 'Lead',
};

/**
 * The linked record. A company without HubSpot ID is the company name as
 * typed (brief section 3, Company links): the temporary Company field of New
 * project gives that until the link picker exists.
 */
export interface RoiLink {
  kind: RoiLinkKind;
  /** The record's name as shown (company name, contact name, deal name). */
  label: string;
  /** The client's company, shown first in the list. */
  company: string;
  portalId: string | null;
  hubspotId: string | null;
}

const name = z.string().trim().min(1).max(200);

export const roiLinkSchema = z
  .object({
    kind: z.enum(ROI_LINK_KINDS),
    label: name,
    company: name,
    portalId: z.uuid().nullable().default(null),
    hubspotId: z.string().trim().min(1).max(40).nullable().default(null),
  })
  .refine((l) => l.kind !== 'lead' || l.portalId !== null, { message: 'a lead is in the portal' })
  .refine((l) => l.kind === 'company' || l.portalId !== null || l.hubspotId !== null, {
    message: 'a contact or a deal is a record of the portal or HubSpot',
  });

export const ROI_SECTIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
/** Open when a project is created, as in the prototype. Sections 6 to 8 open on demand. */
export const ROI_DEFAULT_OPEN_SECTIONS = [1, 2, 3, 4, 5];
/** Always in the PDF; 6, 7 and 8 only when open on screen (brief section 7.7). */
export const ROI_PDF_SECTIONS = [1, 2, 3, 4, 5];

export const roiSectionsSchema = z
  .array(z.number().int().min(1).max(8))
  .max(8)
  .transform((s) => [...new Set(s)].sort((a, b) => a - b));

/**
 * A number field: a number, or '' for an empty field (the engine reads it as
 * 0, as the simulator did). The kg per pick is a slider of the simulator,
 * between 0.3 and 3.
 */
const numberField = z.union([z.literal(''), z.number().finite().min(-1e12).max(1e12)]);
const inputShape = Object.fromEntries(
  ROI_NUMBER_INPUTS.map((k) => [
    k,
    k === 'robotKgPerPick' ? z.number().min(0.3).max(3) : numberField,
  ]),
) as Record<(typeof ROI_NUMBER_INPUTS)[number], typeof numberField>;

export const roiInputsPatchSchema = z
  .object({ ...inputShape, hoursMode: z.enum(['weekly', 'total']) })
  .partial()
  .strict();

export type RoiInputsPatch = Partial<RoiInputs>;

export const roiNewProjectSchema = z.object({
  id: z.uuid(),
  calculator: z.enum(ROI_CALCULATOR_IDS as [string, ...string[]]),
  scenarioName: name,
  link: roiLinkSchema,
});

export const roiProjectPatchSchema = z
  .object({
    /** When the change was made on the device: the last change wins, field by field. */
    changedAt: z.iso.datetime({ offset: true }),
    inputs: roiInputsPatchSchema.optional(),
    openSections: roiSectionsSchema.optional(),
  })
  .strict();

export const roiDuplicateSchema = z.object({ id: z.uuid(), scenarioName: name });

/** A project as the API answers it. */
export interface RoiProject {
  id: string;
  calculator: string;
  scenarioName: string;
  link: RoiLink;
  inputs: RoiInputs;
  openSections: number[];
  createdBy: { id: string; name: string | null };
  createdAt: string;
  updatedAt: string;
  /** Whether the signed-in user may change and delete it (author or admin). */
  canChange: boolean;
}

/** A deleted project, as the admin's restore list shows it. */
export interface RoiDeletedProject {
  id: string;
  calculator: string;
  scenarioName: string;
  link: RoiLink;
  createdBy: { id: string; name: string | null };
  deletedBy: { id: string; name: string | null } | null;
  deletedAt: string;
  /** After this date, the daily purge removes it for good. */
  purgeAt: string;
}

/** List filters of the ROI page (brief section 7.7). */
export const ROI_PERIODS = ['all', '30', 'year'] as const;
export type RoiPeriod = (typeof ROI_PERIODS)[number];
